import React, { useEffect, useState } from 'react'
import { Modal, Form, Input, Typography, Button, Alert } from 'antd'
import { UserOutlined, MailOutlined } from '@ant-design/icons'
import {
  generateSecurePassword,
  validatePassword,
} from 'src/utils/passwordGenerator'
import LoadingButton from 'src/components/styled/LoadingButton'
import VenueRoleSelect from 'src/components/VenueRoleSelect'
import { VENUE_ADMIN } from 'src/utils/roles'
import {
  getInviteNameValidationError,
  resolveInvitedUserName,
  scrubAutofilledInviterEmail,
} from 'src/utils/venueInviteHelpers'
import InviteConfirmDetails from 'src/screens/overview/customers/InviteConfirmDetails'

const { Item } = Form
const { Text, Title } = Typography

const CreateNewUserForm = ({
  submit,
  form,
  loading,
  error,
  prefilledEmail,
  inviterEmail = '',
  selectedRole = VENUE_ADMIN,
  isFreePlan = false,
  customRoles = [],
  onFreePlanRole,
  inviteSummary = null,
  submitLabel,
}) => {
  const { getFieldDecorator, setFieldsValue, getFieldValue } = form
  // Pre-fill so Generate is not a hidden prerequisite below the fold.
  const [initialPassword] = useState(() => generateSecurePassword())
  const [generatedPassword, setGeneratedPassword] = useState(initialPassword)
  const [showPassword, setShowPassword] = useState(true)
  const [passwordMode, setPasswordMode] = useState('generated') // 'none', 'generated', 'manual'
  const [role, setRole] = useState(selectedRole)
  const isSupplierInvite = Boolean(inviteSummary)

  // Clear autofilled inviter email after the browser paints into the name field.
  useEffect(() => {
    const clearAutofill = () => {
      const current = getFieldValue('inviteeFullName')
      const scrubbed = scrubAutofilledInviterEmail(
        current,
        prefilledEmail,
        inviterEmail,
      )
      if (scrubbed !== String(current || '').trim()) {
        setFieldsValue({ inviteeFullName: scrubbed })
      }
    }
    clearAutofill()
    const t1 = setTimeout(clearAutofill, 50)
    const t2 = setTimeout(clearAutofill, 300)
    const t3 = setTimeout(clearAutofill, 800)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
      clearTimeout(t3)
    }
  }, [prefilledEmail, inviterEmail, getFieldValue, setFieldsValue])

  const handleGeneratePassword = () => {
    const newPassword = generateSecurePassword()
    setGeneratedPassword(newPassword)
    setPasswordMode('generated')
    setShowPassword(true)
    form.setFieldsValue({ password: newPassword })
  }

  const handlePasswordChange = e => {
    const value = e.target.value
    if (value && value.length > 0) {
      setPasswordMode('manual')
      setGeneratedPassword('') // Clear generated banner when user types their own
    } else {
      setPasswordMode('none')
      setGeneratedPassword('')
    }
  }

  const handleNameChange = e => {
    const scrubbed = scrubAutofilledInviterEmail(
      e.target.value,
      prefilledEmail,
      inviterEmail,
    )
    // If the browser dumped an email that isn't the invitee, clear it immediately.
    if (scrubbed !== String(e.target.value || '').trim()) {
      setFieldsValue({ inviteeFullName: '' })
    }
  }

  const handleSubmit = () => {
    form.validateFields((validationError, values) => {
      if (!validationError) {
        // Validate password if user entered one
        if (values.password) {
          const validation = validatePassword(values.password)
          if (!validation.valid) {
            form.setFields({
              password: {
                value: values.password,
                errors: [new Error(validation.error)],
              },
            })
            return
          }
        }

        const email = String(values.email || prefilledEmail || '')
          .trim()
          .toLowerCase()
        const scrubbedName = scrubAutofilledInviterEmail(
          values.inviteeFullName,
          email,
          inviterEmail,
        )
        submit({
          ...values,
          email,
          // Blank / mistaken inviter-email → invitee email (never inviter).
          name: resolveInvitedUserName(scrubbedName, email),
          password: values.password || generatedPassword,
          role: role,
        })
      } else {
        console.log('Validation Error', validationError)
      }
    })
  }

  // Custom password validator
  const validatePasswordField = (rule, value, callback) => {
    if (!value) {
      callback('Temporary password is required')
    } else {
      const validation = validatePassword(value)
      if (!validation.valid) {
        callback(validation.error)
      } else {
        callback()
      }
    }
  }

  const validateInviteNameField = (rule, value, callback) => {
    const scrubbed = scrubAutofilledInviterEmail(
      value,
      prefilledEmail,
      inviterEmail,
    )
    const error = getInviteNameValidationError(scrubbed, prefilledEmail)
    if (error) {
      callback(error)
      return
    }
    callback()
  }

  const passwordSection = (
    <PasswordSection>
      <Item
        label={
          <span>
            Temporary password <RequiredMark>(required)</RequiredMark>
          </span>
        }
        help={
          passwordMode === 'manual'
            ? 'You chose this password. It will be emailed to them as their temporary password — use the same wording when you speak to them.'
            : 'A temporary password is ready. Keep it, generate a new one, or type your own. We email this temporary password to them.'
        }
        style={{ marginBottom: 8 }}
      >
        {getFieldDecorator('password', {
          initialValue: initialPassword,
          rules: [{ validator: validatePasswordField }],
        })(
          <Input.Group compact>
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder='Type a temporary password, or generate one'
              disabled={loading}
              style={{ width: 'calc(100% - 120px)' }}
              onChange={handlePasswordChange}
              autoComplete='new-password'
              name='invitee-temporary-password'
              id='fld-invite-temporary-password'
              suffix={
                <Button
                  type='link'
                  icon='eye'
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ padding: '0 4px' }}
                  aria-label={
                    showPassword
                      ? 'Hide temporary password'
                      : 'Show temporary password'
                  }
                />
              }
            />
            <Button
              id='btn-generate-temporary-password'
              onClick={handleGeneratePassword}
              disabled={loading}
              style={{ width: '120px' }}
            >
              {passwordMode === 'generated' ? 'Generate new' : 'Generate'}
            </Button>
          </Input.Group>,
        )}
      </Item>

      {generatedPassword && passwordMode === 'generated' && (
        <Alert
          message={
            <div>
              <Text strong>Temporary password (will be emailed):</Text>
              <br />
              <Text code style={{ fontSize: '16px', letterSpacing: '1px' }}>
                {generatedPassword}
              </Text>
            </div>
          }
          type='info'
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}
    </PasswordSection>
  )

  return (
    <Form
      onSubmit={event => {
        event.preventDefault()
        if (!loading) {
          handleSubmit()
        }
      }}
      autoComplete='off'
    >
      {/* Honey-pot-ish decoys so password managers fill these instead of real fields */}
      <input
        type='text'
        name='username'
        autoComplete='username'
        tabIndex={-1}
        aria-hidden='true'
        style={{
          position: 'absolute',
          left: -9999,
          height: 0,
          width: 0,
          opacity: 0,
        }}
      />
      <input
        type='email'
        name='email'
        autoComplete='email'
        tabIndex={-1}
        aria-hidden='true'
        style={{
          position: 'absolute',
          left: -9999,
          height: 0,
          width: 0,
          opacity: 0,
        }}
      />

      {error && (
        <Alert
          message={error}
          type='error'
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      {inviteSummary && (
        <div style={{ marginBottom: 16 }}>
          <InviteConfirmDetails
            supplierName={inviteSummary.supplierName}
            venueName={inviteSummary.venueName}
            recipientEmail={inviteSummary.recipientEmail || prefilledEmail}
            accountNumber={inviteSummary.accountNumber}
            priceFileName={inviteSummary.priceFileName}
            journey='new'
            compact
          />
        </div>
      )}

      {/* Keep email in the form model; hide the control when already confirmed above */}
      <Item
        label='Email address'
        style={
          isSupplierInvite && prefilledEmail ? { display: 'none' } : undefined
        }
      >
        {getFieldDecorator('email', {
          rules: [
            { required: true, message: 'Please enter an email address' },
            { type: 'email', message: 'Please enter a valid email address' },
          ],
          initialValue: prefilledEmail || '',
        })(
          <Input
            prefix={<MailOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
            placeholder='Email address'
            disabled={loading || !!prefilledEmail}
            autoComplete='off'
            name='invitee-email-address'
          />,
        )}
      </Item>

      <Item label='Full name'>
        {getFieldDecorator('inviteeFullName', {
          initialValue: '',
          rules: [{ validator: validateInviteNameField }],
          validateTrigger: ['onChange', 'onBlur'],
        })(
          <Input
            prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
            placeholder='e.g. Jane Smith (optional)'
            disabled={loading}
            // Do not autoFocus — that triggers browser autofill of the inviter email.
            autoComplete='new-password'
            name='invitee-full-name'
            onChange={handleNameChange}
            onBlur={handleNameChange}
          />,
        )}
      </Item>
      <Text
        type='secondary'
        style={{
          display: 'block',
          marginTop: -12,
          marginBottom: 12,
          fontSize: 12,
        }}
      >
        Optional — leave blank to use{' '}
        <strong>{prefilledEmail || 'their email'}</strong> as the display name
        (never your own login email).
      </Text>

      {/* Password + Generate stay together, immediately before the final action */}
      {passwordSection}

      {/* Role is not sent on supplier-led invite-new; hide to keep the required step visible */}
      {!isSupplierInvite && (
        <Item label='Role'>
          <VenueRoleSelect
            value={role}
            customRoles={customRoles}
            lockCustomRoles={isFreePlan}
            onLockedCustomRole={() => onFreePlanRole?.()}
            tooltipTitle={
              isFreePlan
                ? 'Admin, Head Chef / GM, Chef, Order Only, and Kitchen Porter are on OMS Lite. Custom roles are on Starter.'
                : 'Select the access level for this user. You can adjust it later.'
            }
            onChange={setRole}
            style={{ width: '100%' }}
          />
        </Item>
      )}

      <Item style={{ marginBottom: 0 }}>
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
          }}
        >
          <LoadingButton
            type='primary'
            htmlType='submit'
            loading={loading}
            style={{ width: '100%' }}
            disabled={passwordMode === 'none'}
            id='btn-create-account-send-invitation'
          >
            {submitLabel || 'Create Account & Invite User'}
          </LoadingButton>
        </div>
      </Item>
    </Form>
  )
}

const PasswordSection = ({ children }) => (
  <div style={{ marginBottom: 8 }}>{children}</div>
)

const RequiredMark = ({ children }) => (
  <span style={{ color: '#64748b', fontWeight: 400, fontSize: 12 }}>
    {children}
  </span>
)

const WrappedCreateNewUserForm = Form.create({ name: 'create_new_user' })(
  CreateNewUserForm,
)

// Export the form component props for type checking
export { CreateNewUserForm }

const CreateNewUserModal = ({
  visible,
  onCancel,
  onSubmit,
  loading,
  error,
  prefilledEmail,
  inviterEmail = '',
  selectedRole = VENUE_ADMIN,
  isFreePlan = false,
  customRoles = [],
  onFreePlanRole,
  inviteSummary = null,
}) => {
  const isSupplierInvite = Boolean(inviteSummary)

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {isSupplierInvite
              ? 'Confirm invitation — new OMS user'
              : 'Create New User Account'}
          </Title>
          <Text type='secondary' style={{ fontSize: '14px' }}>
            {isSupplierInvite
              ? 'Set their temporary password below, then send the invitation. They use that password to sign in — it is not confirmation that they have accepted yet.'
              : prefilledEmail
              ? `${prefilledEmail} doesn't have an OMS account yet. We'll create one and send an invitation email.`
              : 'Create a new OMS account for this person'}
          </Text>
        </div>
      }
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={isSupplierInvite ? 560 : 500}
      style={isSupplierInvite ? { top: 24 } : undefined}
      bodyStyle={
        isSupplierInvite
          ? { maxHeight: 'min(70vh, 640px)', overflowY: 'auto' }
          : undefined
      }
      maskClosable={!loading}
      closable={!loading}
      keyboard={!loading}
      destroyOnClose
    >
      {visible ? (
        <WrappedCreateNewUserForm
          submit={onSubmit}
          loading={loading}
          error={error}
          prefilledEmail={prefilledEmail}
          inviterEmail={inviterEmail}
          selectedRole={selectedRole}
          isFreePlan={isFreePlan}
          customRoles={customRoles}
          onFreePlanRole={onFreePlanRole}
          inviteSummary={inviteSummary}
          submitLabel={
            isSupplierInvite ? 'Create account & send invitation' : undefined
          }
        />
      ) : null}
    </Modal>
  )
}

export default CreateNewUserModal
